"use client";

import { getSlateSpacingPixels } from "@faire/retailer-shared/SlateMappings/getSlateSpacingPixels";
import { Column } from "@faire/web--source/slate/Layout";
import { useResponsiveValue } from "@faire/web--source/ui/hooks/useResponsiveValue";
import { WhenVisibilityIsImminent } from "@faire/web--source/ui/VisibilitySensor/WhenVisibilityIsImminent";
import { IGridComponent } from "@faire/web-api--source/indigofair/data/IGridComponent";
import { IGridStyle } from "@faire/web-api--source/indigofair/data/IGridStyle";
import { ITile } from "@faire/web-api--source/indigofair/data/ITile";
import { Spacing } from "@faire/web-api--source/indigofair/data/Spacing";
// eslint-disable-next-line no-restricted-imports
import { assignInlineVars } from "@vanilla-extract/dynamic";
import classNames from "classnames";
import React, { type JSX, PropsWithChildren, useContext } from "react";

import { convertServerResponsivePropertyToClientResponsiveProperty } from "@faire/retailer/components/SDUI/__internal__/Styling/responsiveValues";
import {
  ComponentLoggingEventParams,
  SDUIComponentRenderBehaviour,
} from "@faire/retailer/components/SDUI/__internal__/types";
import * as v2GridStyles from "@faire/retailer/components/SDUI/ComponentRenderers/Grid/__internal__/GridV2/v2Styles.css";
import { SDUIGridHeaderRow } from "@faire/retailer/components/SDUI/ComponentRenderers/Grid/__internal__/SDUIGridHeaderRow";
import * as gridStyles from "@faire/retailer/components/SDUI/ComponentRenderers/Grid/__internal__/styles.css";
// eslint-disable-next-line @faire/enforce-internal-folder-encapsulation
import { FilterablePaginatedSectionContext } from "@faire/retailer/components/SDUI/SectionRenderers/FilterablePaginatedSection/__internal__/FilterablePaginatedSectionContext";

import {
  GridV2BrandTilePlaceholder,
  GridV2GenericTilePlaceholder,
} from "./GridV2Placeholders";
import { GridV2ProductTilePlaceholder } from "./GridV2Placeholders";
import { SDUIGridV2Tile } from "./SDUIGridV2Tile";
import { getGridFixedLayoutResponsiveStyleVars } from "./v2FixedLayoutStylingUtils";

const FALLBACK_NUMBER_OF_TILES_PER_ROW = 2;

export const SDUIGridV2FixedColumnLayoutBase: React.FC<{
  gridComponent: IGridComponent;
  loggingData: ComponentLoggingEventParams;
  isRequiredForInteractivity?: boolean;
  renderBehaviour?: SDUIComponentRenderBehaviour;
}> = ({
  gridComponent,
  isRequiredForInteractivity,
  loggingData,
  renderBehaviour,
}) => {
  const {
    column_layout = IGridComponent.IColumnLayout.build(),
    header_background_icon,
    responsive_header_background_icon,
    responsive_title,
    style = IGridStyle.build(),
    tiles,
    title,
    log_tile_batch_view,
  } = gridComponent;

  const {
    num_columns = FALLBACK_NUMBER_OF_TILES_PER_ROW,
    responsive_num_columns,
  } = column_layout.fixed ?? IGridComponent.IFixedColumnLayout.build();
  const {
    column_spacing = Spacing.SPACING_NONE,
    row_spacing = Spacing.SPACING_NONE,
  } = style;

  const columnSpacingPixels = getSlateSpacingPixels(column_spacing);
  const rowSpacingPixels = getSlateSpacingPixels(row_spacing);

  const responsiveNumberOfTilesPerRow =
    convertServerResponsivePropertyToClientResponsiveProperty(
      num_columns,
      (col) => col,
      responsive_num_columns
    );

  const numTilesPerRow = useResponsiveValue(responsiveNumberOfTilesPerRow);

  return (
    <Column className={gridStyles.SDUIGridWrapper}>
      <SDUIGridHeaderRow
        title={title}
        responsiveTitle={responsive_title}
        backgroundIcon={header_background_icon}
        responsiveBackgroundIcon={responsive_header_background_icon}
        loggingData={loggingData}
      />
      <div
        className={classNames(
          v2GridStyles.gridTileContainer,
          v2GridStyles.gridFixedLayout
        )}
        style={assignInlineVars({
          [v2GridStyles.varRowGap]: `${rowSpacingPixels}px`,
          [v2GridStyles.varColumnGap]: `${columnSpacingPixels}px`,
          ...getGridFixedLayoutResponsiveStyleVars(
            responsiveNumberOfTilesPerRow
          ),
        })}
      >
        {tiles.map((tile, index) => {
          // first row should be visible for UPLT calculations
          const shouldForceVisibility =
            index < numTilesPerRow && isRequiredForInteractivity;
          return (
            <MaybeWhenVisibilityIsImminent
              key={tile.logging_data?.instance_identifier ?? index}
              forceVisibility={shouldForceVisibility}
              placeholder={getRowPlaceholder(tile)}
              renderBehaviour={renderBehaviour}
            >
              <SDUIGridV2Tile
                key={tile.logging_data?.instance_identifier ?? index}
                tile={tile}
                loggingData={loggingData}
                logTileBatchView={log_tile_batch_view}
                rowSpacingPixels={rowSpacingPixels}
                shouldSetIsScrollingTopToFalseOnView={
                  index === 0 &&
                  isRequiredForInteractivity &&
                  renderBehaviour?.gridOptimizations
                }
              />
            </MaybeWhenVisibilityIsImminent>
          );
        })}
      </div>
    </Column>
  );
};

const getRowPlaceholder = (tile?: ITile) => {
  if (!tile) {
    return undefined;
  }

  if (tile.brand_tile || tile.brand_tile_with_action) {
    return <GridV2BrandTilePlaceholder />;
  } else if (tile.product_tile || tile.product_with_brand_info_tile) {
    return <GridV2ProductTilePlaceholder />;
  }

  return <GridV2GenericTilePlaceholder />;
};

const MaybeWhenVisibilityIsImminent: React.FC<
  PropsWithChildren & {
    forceVisibility?: boolean;
    placeholder?: JSX.Element;
    renderBehaviour?: SDUIComponentRenderBehaviour;
  }
> = ({ forceVisibility, placeholder, children, renderBehaviour }) => {
  const { isScrollingToTop } = useContext(FilterablePaginatedSectionContext);
  if (
    !forceVisibility &&
    isScrollingToTop &&
    renderBehaviour?.gridOptimizations
  ) {
    if (placeholder) {
      return placeholder;
    }
  }
  if (!forceVisibility && placeholder) {
    return (
      <WhenVisibilityIsImminent customizePlaceholder={placeholder}>
        {children}
      </WhenVisibilityIsImminent>
    );
  }

  return children;
};
